import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeSlash, CheckLg, XLg } from "react-bootstrap-icons";

/* API helper */
async function putJson(url, body) {
  const token = localStorage.getItem("authToken");

  const res = await fetch(url, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.message || "Request failed");
  }

  return data;
}

export default function ChangePassword() {
  const USER_API = process.env.REACT_APP_USER_SERVICE_URL;
  const navigate = useNavigate();

  const [form, setForm] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [ui, setUi] = useState({
    showOld: false,
    showNew: false,
    showConfirm: false,
    loading: false,
    error: "",
    success: "",
  });

  // ===============================
  // Password Rules
  // ===============================
  const rules = {
    length: form.newPassword.length >= 6,
    uppercase: /[A-Z]/.test(form.newPassword),
    number: /[0-9]/.test(form.newPassword),
    special: /[^A-Za-z0-9]/.test(form.newPassword),
  };

  const score = Object.values(rules).filter(Boolean).length;
  const percent = (score / 4) * 100;
  const strengthClass =
    score <= 1 ? "bg-danger" : score === 2 ? "bg-warning" : "bg-success";

  // ===============================
  // Validation
  // ===============================
  const validate = useCallback(() => {
    if (!form.oldPassword || !form.newPassword || !form.confirmPassword)
      return "All fields are required";

    if (!rules.length || !rules.uppercase || !rules.number || !rules.special)
      return "Password must include uppercase, number and special character";

    if (form.newPassword !== form.confirmPassword)
      return "Passwords do not match";

    if (form.oldPassword === form.newPassword)
      return "New password must be different";

    return null;
  }, [form, rules]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((s) => ({ ...s, [name]: value }));
    setUi((u) => ({ ...u, error: "", success: "" }));
  };

  // ===============================
  // Submit
  // ===============================
  const handleSubmit = async (e) => {
    e.preventDefault();

    const error = validate();
    if (error) {
      return setUi((u) => ({ ...u, error }));
    }

    try {
      setUi((u) => ({ ...u, loading: true, error: "" }));

      await putJson(`${USER_API}/sellers/change-password`, {
        oldPassword: form.oldPassword,
        newPassword: form.newPassword,
        confirmPassword: form.confirmPassword,
      });

      setUi({
        loading: false,
        error: "",
        success: "Password changed successfully. Please login again",
        showOld: false,
        showNew: false,
        showConfirm: false,
      });
      setForm({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });

      setTimeout(() => {
        localStorage.removeItem("authToken");
        navigate("/login");
      });
    } catch (err) {
      setUi((u) => ({
        ...u,
        loading: false,
        error: err.message,
      }));
    }
  };

  const Rule = ({ valid, text }) => (
    <li className={valid ? "text-success" : "text-danger"}>
      {valid ? <CheckLg /> : <XLg />} {text}
    </li>
  );

  return (
    <div className="container mt-4" style={{ maxWidth: 500 }}>
      <div className="card shadow-sm">
        <div className="card-body">
          <h4 className="mb-3">Change Password</h4>

          {ui.error && <div className="alert alert-danger">{ui.error}</div>}
          {ui.success && (
            <div className="alert alert-success">{ui.success}</div>
          )}

          <form onSubmit={handleSubmit}>
            {/* OLD PASSWORD */}
            <div className="mb-3">
              <label className="form-label">Current Password</label>
              <div className="input-group">
                <input
                  name="oldPassword"
                  type={ui.showOld ? "text" : "password"}
                  className="form-control"
                  value={form.oldPassword}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setUi((u) => ({ ...u, showOld: !u.showOld }))}
                >
                  {ui.showOld ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            {/* NEW PASSWORD */}
            <div className="mb-3">
              <label className="form-label">New Password</label>
              <div className="input-group">
                <input
                  name="newPassword"
                  type={ui.showNew ? "text" : "password"}
                  className="form-control"
                  value={form.newPassword}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setUi((u) => ({ ...u, showNew: !u.showNew }))}
                >
                  {ui.showNew ? <EyeSlash /> : <Eye />}
                </button>
              </div>

              {/* Strength */}
              {form.newPassword && (
                <>
                  <div className="progress mt-2" style={{ height: 6 }}>
                    <div
                      className={`progress-bar ${strengthClass}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>

                  <ul className="small mt-2 list-unstyled">
                    <Rule valid={rules.length} text="6+ characters" />
                    <Rule valid={rules.uppercase} text="Uppercase letter" />
                    <Rule valid={rules.number} text="Number" />
                    <Rule valid={rules.special} text="Special character" />
                  </ul>
                </>
              )}
            </div>

            {/* CONFIRM */}
            <div className="mb-3">
              <label className="form-label">Confirm Password</label>
              <div className="input-group">
                <input
                  name="confirmPassword"
                  type={ui.showConfirm ? "text" : "password"}
                  className="form-control"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setUi((u) => ({ ...u, showConfirm: !u.showConfirm }))
                  }
                >
                  {ui.showConfirm ? <EyeSlash /> : <Eye />}
                </button>
              </div>
            </div>

            <button
              className="btn w-100"
              type="submit"
              disabled={ui.loading}
              style={{
                backgroundColor: "#0e377d",
                color: "#fff",
              }}
            >
              {ui.loading ? "Saving..." : "Change Password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
